import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { AssetTimelineParams } from '@zero/shared/util-models';
import { instrumentDetailActions } from '../+state/instrument-detail.actions';
import { instrumentDetailFeature } from '../+state/instrument-detail.reducer';
import { selectAssetTimelineHasNext, selectAssetTimelineItems } from '../+state/instrument-detail.selectors';

@Injectable({ providedIn: 'root' })
export class InstrumentDetailFacade {
  readonly #store = inject(Store);

  public readonly assetTimeline$ = this.#store.select(instrumentDetailFeature.selectAssetTimeline);
  public readonly assetTimelineItems$ = this.#store.select(selectAssetTimelineItems);
  public readonly assetTimelineHasNext$ = this.#store.select(selectAssetTimelineHasNext);
  public readonly assetTimelineError$ = this.#store.select(instrumentDetailFeature.selectAssetTimelineError);
  public readonly assetTimelineLoading$ = this.#store.select(instrumentDetailFeature.selectAssetTimelineLoading);
  public readonly assetTimelineInitiallyLoaded$ = this.#store.select(instrumentDetailFeature.selectAssetTimelineInitiallyLoaded);
  public readonly assetTimelineInfo$ = this.#store.select(instrumentDetailFeature.selectAssetTimelineInfo);
  public readonly derivativeTopData$ = this.#store.select(instrumentDetailFeature.selectDerivativeTopData);
  public readonly derivativeBaseData$ = this.#store.select(instrumentDetailFeature.selectDerivativeBaseData);
  public readonly derivativeKeyData$ = this.#store.select(instrumentDetailFeature.selectDerivativeKeyData);
  public readonly derivativePayoutProfile$ = this.#store.select(instrumentDetailFeature.selectDerivativePayoutProfile);
  public readonly derivativeBaseDataVisible$ = this.#store.select(instrumentDetailFeature.selectDerivativeBaseDataVisible);
  public readonly derivativeKeyDataVisible$ = this.#store.select(instrumentDetailFeature.selectDerivativeKeyDataVisible);
  public readonly derivativePayoutProfileVisible$ = this.#store.select(instrumentDetailFeature.selectDerivativePayoutProfileVisible);
  public readonly derivativeProductDescription$ = this.#store.select(instrumentDetailFeature.selectDerivativeProductDescription);
  public readonly derivativeUnderlyingData$ = this.#store.select(instrumentDetailFeature.selectDerivativeUnderlyingData);

  public loadAssetTimelineInfo(isin: string) {
    this.#store.dispatch(instrumentDetailActions.loadAssetTimelineInfo({ isin }));
  }

  public loadAssetTimeline(params: AssetTimelineParams, force = false): void {
    this.#store.dispatch(instrumentDetailActions.loadAssetTimeline({ force, params }));
  }

  public clear() {
    this.clearAssetTimeline();
    this.#store.dispatch(instrumentDetailActions.resetDerivativeData());
  }

  public clearAssetTimeline() {
    this.#store.dispatch(instrumentDetailActions.resetAssetTimeline());
  }

  public toggleDerivativeDataVisibility(type: 'base' | 'key' | 'payout') {
    if (type === 'base') {
      this.#toggleDerivativeBaseDataVisibility();
    } else if (type === 'key') {
      this.#toggleDerivativeKeyDataVisibility();
    } else {
      this.#toggleDerivativePayoutProfileVisibility();
    }
  }

  public loadDerivativeData(isin: string): void {
    this.#store.dispatch(instrumentDetailActions.loadDerivativeData({ isin }));
    this.#store.dispatch(instrumentDetailActions.checkDerivativeDataVisibility());
  }

  #toggleDerivativeBaseDataVisibility() {
    this.#store.dispatch(instrumentDetailActions.toggleDerivativeBaseDataVisibility());
  }

  #toggleDerivativeKeyDataVisibility() {
    this.#store.dispatch(instrumentDetailActions.toggleDerivativeKeyDataVisibility());
  }

  #toggleDerivativePayoutProfileVisibility() {
    this.#store.dispatch(instrumentDetailActions.toggleDerivativePayoutProfileVisibility());
  }
}
